import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { TABLE_RECORD_PER_PAGE } from "../../../constants";
import { apiEndpoints, httpMethods } from "../../../constants/api.constants";
import {
  addProductColorFields,
  loaderPath,
  productColorTableColumns,
} from "../../../descriptions/settings.descriptions";
import useAPI from "../../../hooks/useAPI";
import useDebounce from "../../../hooks/useDebounce";
import useI18N from "../../../hooks/useI18N";
import usePagination from "../../../hooks/usePagination";
import { addProductColor } from "../../../redux/slices/settings.slices";
import { addRowsPerPage } from "../../../redux/slices/table.slices";
import { Edit, Search } from "../../../shared/icon";
import { MUIStyled } from "../../../shared/MUIStyled";
import SABasicTable from "../../../shared/SABasicTable";
import SAButton from "../../../shared/SAButton";
import SACard from "../../../shared/SACard";
import SAStack from "../../../shared/SAStack";
import SAStatusBadge from "../../../shared/SAStatusBadge";
import SATextField from "../../../shared/SATextField";
import SATruncateWithEllipsis from "../../../shared/SATruncateWithEllipsis";
import SATypography from "../../../shared/SATypography";
import { validTableValue } from "../../../utils/functions";
import AddEditModal from "./AddEditModal";
import AddProductColorModal from "./AddProductColorModal";

const SearchWrapper = MUIStyled(SAStack)({
  maxWidth: 400,
  width: "100%",
});

const ProductsColor = () => {
  const { t } = useI18N();
  const { api } = useAPI();
  const dispatch = useDispatch();

  const [addProductColors, setAddProductColors] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [edit, setEdit] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const loading = useSelector((state) => ({
    getProductColor: state.loader?.[loaderPath.getProductColor],
    getProductColorStatus: state.loader?.[loaderPath.getProductColorStatus],
  }));
  const productColorList = useSelector(
    (state) => state.settings?.productColorList,
  );

  const debouncedSearch = useDebounce(searchTerm, 300, () => setCurrentPage(1));
  const { count, results: productColorData } = productColorList || {};

  const tableRows = useSelector((state) => state?.rowsPerPage?.productColor);

  const {
    totalPage,
    currentPage,
    itemPerPage,
    setCurrentPage,
    setItemPerPage,
  } = usePagination(count, tableRows || TABLE_RECORD_PER_PAGE);

  const handleSetItemPerPage = (val) => {
    setItemPerPage(val);
    dispatch(addRowsPerPage({ name: "productColor", data: val }));
  };

  useEffect(() => {
    callProductTypeAPI();
  }, [currentPage, itemPerPage, debouncedSearch]);

  const handleSearchChange = (e) => setSearchTerm(e.target.value);

  const getProductTypeList = async ({ page, page_size, name = "" } = {}) => {
    const response = await api({
      method: httpMethods.get,
      endPoint: apiEndpoints.getProductColor,
      needLoader: true,
      loader: loaderPath.getProductColor,
      params: { page, page_size, name, ordering: "-created_at" },
      showToastOnError: true,
    });

    if (response?.status) {
      dispatch(addProductColor({ data: response?.data }));
    }
  };

  const callProductTypeAPI = () => {
    getProductTypeList({
      page: currentPage,
      page_size: itemPerPage,
      name: debouncedSearch,
    });
  };

  const handleStatusChange = async (data) => {
    const { id, is_enabled } = data || {};
    if (!id) return;

    setSelectedProduct(data?.id);

    const response = await api({
      method: httpMethods.patch,
      endPoint: `${apiEndpoints.getProductColor}${id}/`,
      data: { is_enabled: !is_enabled },
      needLoader: true,
      loader: loaderPath.getProductColorStatus,
      showToastMessage: true,
    });

    if (response?.status) callProductTypeAPI();

    setSelectedProduct(null);
  };

  const onCloseModal = () => {
    setAddProductColors(false);
    setEdit(false);
  };

  const paginationProps = {
    currentPage,
    setItemPerPage: handleSetItemPerPage,
    setCurrentPage,
    page: totalPage,
    rowsPerPage: itemPerPage,
  };

  const handleEdit = (data) => {
    setSelectedProduct(data);
    setEdit(true);
  };

  const primaryActionStyle = {
    size: "small",
    variant: "outlined",
    sx: { "& svg": { ml: 1 } },
  };

  const renderCell = (columnKey, data) => {
    switch (columnKey) {
      case "name":
        return (
          <SATypography variant="body2" fontWeight={600}>
            <SATruncateWithEllipsis
              maxLength={25}
              text={validTableValue(data?.name)}
            />
          </SATypography>
        );

      case "status":
        return (
          <SAStatusBadge status={data?.is_enabled ? "ENABLED" : "DISABLED"} />
        );

      case "action":
        return (
          <SAButton
            size="small"
            variant="outlined"
            onClick={() => handleStatusChange(data)}
            loading={
              selectedProduct === data?.id && loading.getProductColorStatus
            }
          >
            {t(data?.is_enabled ? "disable" : "enable")}
          </SAButton>
        );

      case "edit":
        return (
          <SAButton
            size="small"
            variant="outlined"
            onClick={() => handleEdit(data)}
            {...primaryActionStyle}
          >
            {t("edit-button")}
            {<Edit />}
          </SAButton>
        );

      default:
        return data[columnKey];
    }
  };

  return (
    <>
      <SACard>
        <SAStack
          pb={2}
          mb={1}
          alignItems="center"
          direction="row"
          gap={2}
          justifyContent="space-between"
          borderBottom="1px solid gray.main 50"
        >
          <SearchWrapper>
            <SATextField
              value={searchTerm}
              onChange={handleSearchChange}
              placeholder={t("search-for-product-color")}
              InputProps={{ startAdornment: <Search /> }}
            />
          </SearchWrapper>
          <SAButton
            color="primary"
            variant="contained"
            onClick={() => setAddProductColors(true)}
          >
            {t("add-product-color")}
          </SAButton>
        </SAStack>
        <SABasicTable
          {...{
            renderCell,
            paginationProps,
            data: productColorData,
            loading: loading.getProductColor,
            columns: productColorTableColumns,
          }}
        />
      </SACard>
      <AddProductColorModal
        open={addProductColors}
        onClose={onCloseModal}
        onSuccess={callProductTypeAPI}
      />
      <AddEditModal
        open={edit}
        onClose={onCloseModal}
        onSuccess={callProductTypeAPI}
        defaultValues={selectedProduct}
        apiConfig={{
          endPoint: apiEndpoints.getProductColor,
          loaderKey: loaderPath.addEditModal,
          titleKey: edit
            ? "edit-product-color-title"
            : "add-product-color-title",
          submitLabelKey: edit ? "save-changes" : "add-product-color",
          fields: addProductColorFields,
        }}
      />
    </>
  );
};

export default ProductsColor;
